import { combineAccessInputs, mapAccessInput, type AccessInput } from '@/abilities/accessInput';
import { createCaseAbility } from '@/abilities/createCaseAbility';
import { treeDestinationAccess, type DestinationAccess } from '@/abilities/destination';
import {
  companySettingsAccessContext,
  type CompanySettingsAccessContext,
  type CompanySettingsAccessValues,
} from '@/abilities/companySettings/context';
import { companySettingsRules } from '@/abilities/companySettings/rules';
import {
  companySettingsDestinationTree,
  toCompanySettingsDestination,
  type CompanySettingsAction,
  type CompanySettingsDestination,
  type CompanySettingsDestinationKey,
} from '@/abilities/companySettings/vocabulary';

/**
 * How the user may reach each settings destination: a destination with nothing under it from its rules, a tab with sections and
 * the settings screen from their children. The only answer the case gives — a CASL check on a tab with sections would
 * find no rule and always say no.
 */
export type CompanySettingsAccess = {
  readonly accessOf: (destination: CompanySettingsDestinationKey) => DestinationAccess;
};

export type CompanySettingsAccessInputs = {
  readonly [Key in keyof CompanySettingsAccessValues]: AccessInput<CompanySettingsAccessValues[Key]>;
};

export const companySettingsAccessOf = (ctx: CompanySettingsAccessContext): CompanySettingsAccess => ({
  accessOf: treeDestinationAccess(
    createCaseAbility<CompanySettingsAction, CompanySettingsDestination>(companySettingsRules(ctx)),
    toCompanySettingsDestination,
    companySettingsDestinationTree,
  ),
});

export const companySettingsAccess = (inputs: CompanySettingsAccessInputs): AccessInput<CompanySettingsAccess> =>
  mapAccessInput(combineAccessInputs(inputs), (values) =>
    companySettingsAccessOf(companySettingsAccessContext(values)),
  );
